/**
 * One row of the account menu — the same row wherever the menu appears: the
 * my-account sidebar, its mobile "More" sheet and the header menu's Account
 * section. Renders as a link, a button or (disabled) an inert div, so the
 * callers decide the behaviour and this component owns the look.
 */

import type { ReactNode } from "react";
import { NavIcon, type NavIconName } from "@/components/icons/navIcons";
import styles from "./AccountNavRow.module.css";

export interface AccountNavRowProps {
  icon: NavIconName;
  label: string;
  /** Renders an anchor. Without it the row is a button. */
  href?: string;
  onClick?: () => void;
  /** The row for the page the member is on. */
  active?: boolean;
  /**
   * A "coming soon" row: listed so the menu matches WeHunt Web's, but inert.
   * Rendered as a div so it is neither focusable nor announced as a control.
   */
  disabled?: boolean;
  /** Tooltip for a disabled row, e.g. "Coming soon". */
  disabledTitle?: string;
  /**
   * "md" is the sidebar row (14px text, 18px glyph); "lg" the header-menu row
   * (16px text, 20px glyph), matching WeHunt Web's two densities.
   */
  size?: "md" | "lg";
  /** Control drawn at the row's right edge, e.g. the dark-mode switch. */
  trailing?: ReactNode;
  /**
   * Makes a button row a toggle: rendered with role="switch" and this checked
   * state, and an accessible name that stays the same whichever way it is
   * flipped (the visible label may change with the state).
   */
  switchChecked?: boolean;
  ariaLabel?: string;
  className?: string;
}

export function AccountNavRow({
  icon,
  label,
  href,
  onClick,
  active = false,
  disabled = false,
  disabledTitle,
  size = "md",
  trailing,
  switchChecked,
  ariaLabel,
  className,
}: AccountNavRowProps) {
  const rowClassName = [
    styles.row,
    size === "lg" ? styles.rowLg : "",
    active ? styles.rowActive : "",
    disabled ? styles.rowDisabled : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      <NavIcon name={icon} className={styles.icon} />
      <span className={styles.label}>{label}</span>
      {trailing}
    </>
  );

  if (disabled) {
    return (
      <div className={rowClassName} aria-disabled="true" title={disabledTitle}>
        {content}
      </div>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        className={rowClassName}
        aria-current={active ? "page" : undefined}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  const isSwitch = switchChecked !== undefined;

  return (
    <button
      type="button"
      className={rowClassName}
      role={isSwitch ? "switch" : undefined}
      aria-checked={isSwitch ? switchChecked : undefined}
      aria-current={active ? "page" : undefined}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {content}
    </button>
  );
}

export default AccountNavRow;
